import { gql, useFragment } from '@apollo/client';
import { Link, Text } from '@apollo/orbit';
import React from 'react';

import { userSettingsRouteConfig } from 'src/app/account/routes';
import { Code } from 'src/components/code/Code';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { usePublishedSchema } from 'src/components/publishSchema/usePublishedSchema';
import { useProvisionDefaultUserToken } from 'src/hooks/useProvisionDefaultUserToken';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  schemaFileToGraphPublish,
  schemaFileToSubgraphPublish,
} from 'src/lib/roverCommands';

import { useGraphRef } from '../hooks/useGraphRef';

export const ContinueOnboardingWidgetFragment = gql<
  GraphQLTypes.ContinueOnboardingWidgetFragment,
  {}
>`
  fragment ContinueOnboardingWidgetFragment on Service {
    id
    apiKeys {
      id
      token
    }
  }
`;

export const ContinueOnboardingWidget = () => {
  const graphRef = useGraphRef();
  const { data } = useFragment({
    fragment: ContinueOnboardingWidgetFragment,
    from: {
      __typename: 'Service',
      id: graphRef?.graphId,
    },
  });

  const { architecture } = usePublishedSchema({
    graphId: graphRef?.graphId,
    graphVariant: graphRef?.graphVariant,
  });
  const { userToken, isProvisionKeyLoading, isProvisionKeyCalled } =
    useProvisionDefaultUserToken();

  if (!graphRef?.graphId && !graphRef?.graphVariant) {
    return null;
  }

  const serviceApiToken = data?.apiKeys?.[0]?.token || '[Token Unavailable]';

  const isFederated = architecture === 'federation';
  const schemaScript = isFederated
    ? schemaFileToSubgraphPublish({
        apiKey: serviceApiToken,
        graphId: graphRef?.graphId,
        graphVariant: graphRef?.graphVariant,
        highlightGraphRef: false,
      })
    : schemaFileToGraphPublish({
        apiKey: serviceApiToken,
        graphId: graphRef.graphId,
        graphVariant: graphRef.graphVariant,
        highlightGraphRef: false,
      });
  return (
    <PermissionGuard
      graphId={graphRef.graphId}
      graphVariant={graphRef.graphVariant}
      graphVariantPermissions="canPushSchemas"
      fallback={null}
    >
      <div className="flex flex-col gap-4 rounded bg-primary-inverted px-4">
        <Text className="text-center">
          To continue,{' '}
          {!userToken && !isProvisionKeyLoading && isProvisionKeyCalled ? (
            <>
              <Link
                to={userSettingsRouteConfig.location({})}
                className="text-link underline"
              >
                create a personal API key
              </Link>{' '}
              and then
            </>
          ) : (
            ''
          )}
          publish your {isFederated ? 'first subgraph' : 'graph'}:
        </Text>
        <Code
          showCopyButton={true}
          firstLineDecorator="$ "
          highlight={schemaScript.shellStringLinesToHighlight}
          mask={serviceApiToken.split(':').pop()}
          code={schemaScript.shellString}
        />
      </div>
    </PermissionGuard>
  );
};
